/**
 * Integration Hooks Context
 * 
 * Provides a system for integrations to dynamically hook into UI slots.
 * Integrations define hooks in their config, and UI components can render
 * integration actions based on defined slots.
 * 
 * @created 2025-12-26
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

// Types for integration hooks
export interface UIHook {
  slot: string;
  label: string;
  icon?: string;
  action: string;
  description?: string;
  params?: Record<string, unknown>;
}

export interface IntegrationConfig {
  configSchema?: Record<string, {
    type: string;
    required: boolean;
    label: string;
    description?: string;
    placeholder?: string;
  }>;
  uiHooks?: UIHook[];
  actions?: Record<string, {
    description: string;
    params: Record<string, { type: string; required: boolean; description: string }>;
    returns: Record<string, { type: string; description: string }>;
  }>;
}

export interface EnabledIntegration {
  id: string;
  name: string;
  description?: string;
  version: string;
  enabled: boolean;
  config?: IntegrationConfig;
  tenantConfig?: Record<string, unknown>;
  definition?: any; // Integration definition from catalog
}

export interface IntegrationHook extends UIHook {
  integrationId: string;
  integrationName: string;
}

interface IntegrationHooksContextType {
  // All enabled integrations for the tenant
  enabledIntegrations: EnabledIntegration[];
  // Loading state
  isLoading: boolean;
  // Get hooks for a specific slot
  getHooksForSlot: (slot: string) => IntegrationHook[];
  // Check if any integrations have hooks for a slot
  hasHooksForSlot: (slot: string) => boolean;
  // Invoke an integration action
  invokeAction: <T = unknown>(
    integrationId: string,
    actionName: string,
    params?: Record<string, unknown>
  ) => Promise<{ success: boolean; result?: T; error?: string }>;
  // Refresh integrations
  refresh: () => Promise<void>;
}

const IntegrationHooksContext = createContext<IntegrationHooksContextType | null>(null);

export function IntegrationHooksProvider({ children }: { children: ReactNode }) {
  const { activeTenancy } = useTenancy();
  const [enabledIntegrations, setEnabledIntegrations] = useState<EnabledIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch enabled integrations for the tenant
  const fetchIntegrations = useCallback(async () => {
    if (!activeTenancy?.id) {
      setEnabledIntegrations([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Fetch tenant's enabled integrations with their configs
      const response = await deiterateAPI.GET('/v1/integrations', {
        backend: 'thalassa',
        headers: {
          'x-tenancy-id': activeTenancy.id,
          'company-id': activeTenancy.id,
          'customer-id': activeTenancy.id,
        },
      }, true);
      
      if (response?.integrations) {
        // console.log('[IntegrationHooks] Fetched integrations:', response.integrations);
        
        // Filter to only enabled integrations and enrich with catalog data
        const enabled = response.integrations
          .filter((i: any) => i.enabled)
          .map((i: any) => ({
            id: i.id || i.integrationId,
            name: i.name || i.integrationId,
            description: i.description,
            version: i.version || '1.0.0',
            enabled: true,
            config: i.config || i.catalogConfig,
            tenantConfig: i.tenantConfig,
            definition: i.definition, // Include the definition field
          }));
        
        // console.log('[IntegrationHooks] Enabled integrations:', enabled);
        setEnabledIntegrations(enabled);
      }
    } catch (error) {
      // console.error('[IntegrationHooks] Error fetching integrations:', error);
      setEnabledIntegrations([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTenancy?.id]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  // Get all hooks for a specific UI slot
  const getHooksForSlot = useCallback((slot: string): IntegrationHook[] => {
    const hooks: IntegrationHook[] = [];
    
    for (const integration of enabledIntegrations) {
      // uiHooks are in the definition.config (from the catalog definition)
      const uiHooks = integration.definition?.config?.uiHooks || [];
      const hooksArray = Array.isArray(uiHooks) ? uiHooks : [];
      // console.log(`[IntegrationHooks] Found ${hooksArray.length} hooks for ${integration.id}:`, hooksArray);
      // console.log(`[IntegrationHooks] Integration definition:`, integration.definition);
      for (const hook of hooksArray) {
        if (hook.slot === slot) {
          hooks.push({
            ...hook,
            integrationId: integration.id,
            integrationName: integration.name,
          });
        }
      }
    }
    
    return hooks;
  }, [enabledIntegrations]);

  // Check if any integration has hooks for a slot
  const hasHooksForSlot = useCallback((slot: string): boolean => {
    return getHooksForSlot(slot).length > 0;
  }, [getHooksForSlot]);

  // Invoke an integration action
  const invokeAction = useCallback(async <T = unknown>(
    integrationId: string,
    actionName: string,
    params: Record<string, unknown> = {}
  ): Promise<{ success: boolean; result?: T; error?: string }> => {
    if (!activeTenancy?.id) {
      // console.error('[IntegrationHooks] No active tenancy');
      return { success: false, error: 'No active tenancy' };
    }
    
    // console.log('[IntegrationHooks] Invoking action:', {
    //   integrationId,
    //   actionName,
    //   params,
    //   tenancyId: activeTenancy.id,
    // });
    
    try {
      const response = await deiterateAPI.POST(
        `/v1/integrations/${integrationId}/actions/${actionName}`,
        { 
          body: JSON.stringify({ params }), 
          backend: 'thalassa',
          headers: {
            'x-tenancy-id': activeTenancy?.id,
            'company-id': activeTenancy?.id,
            'customer-id': activeTenancy?.id,
          },
        },
        true
      );
      
      // console.log('[IntegrationHooks] API response:', response);
      
      if (response?.success) {
        return {
          success: true,
          result: response.result as T,
        };
      }
      
      return {
        success: false,
        error: response?.error || 'Action failed',
      };
    } catch (error: any) {
      // console.error(`[IntegrationHooks] Action ${actionName} failed:`, error);
      return {
        success: false,
        error: error.message || 'Failed to invoke action',
      };
    }
  }, [activeTenancy?.id]);

  const value: IntegrationHooksContextType = {
    enabledIntegrations,
    isLoading,
    getHooksForSlot,
    hasHooksForSlot,
    invokeAction,
    refresh: fetchIntegrations,
  };

  return (
    <IntegrationHooksContext.Provider value={value}>
      {children}
    </IntegrationHooksContext.Provider>
  );
}

export function useIntegrationHooks() {
  const context = useContext(IntegrationHooksContext);
  if (!context) {
    throw new Error('useIntegrationHooks must be used within an IntegrationHooksProvider');
  }
  return context;
}

// Hook to get hooks for a specific slot
export function useSlotHooks(slot: string) {
  const { getHooksForSlot, hasHooksForSlot, isLoading } = useIntegrationHooks();
  return {
    hooks: getHooksForSlot(slot),
    hasHooks: hasHooksForSlot(slot),
    isLoading,
  };
}
